import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../hooks/useTranslation';

const About: React.FC = () => {
  const { t } = useTranslation();

  const stats = [
    { number: '50K+', label: t('about.stats.clients') },
    { number: '100+', label: t('about.stats.destinations') },
    { number: '15+', label: t('about.stats.years') },
    { number: '98%', label: t('about.stats.satisfaction') }
  ];

  const team = [
    {
      name: 'أحمد محمد',
      position: t('about.team.ceo'),
      image: '👨‍💼',
      description: t('about.team.ceo_desc')
    },
    {
      name: 'فاطمة علي',
      position: t('about.team.cto'),
      image: '👩‍💻',
      description: t('about.team.cto_desc')
    },
    {
      name: 'محمد حسن',
      position: t('about.team.cfo'),
      image: '👨‍💼',
      description: t('about.team.cfo_desc')
    },
    {
      name: 'سارة أحمد',
      position: t('about.team.cmo'),
      image: '👩‍💼',
      description: t('about.team.cmo_desc')
    }
  ];

  const values = [
    {
      icon: '🌟',
      title: t('about.values.quality.title'),
      description: t('about.values.quality.desc')
    },
    {
      icon: '🤝',
      title: t('about.values.trust.title'),
      description: t('about.values.trust.desc')
    },
    {
      icon: '💡',
      title: t('about.values.innovation.title'),
      description: t('about.values.innovation.desc')
    },
    {
      icon: '🌍',
      title: t('about.values.sustainability.title'),
      description: t('about.values.sustainability.desc')
    }
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-content"
          >
            <h1 className="hero-title">{t('about.hero.title')}</h1>
            <p className="hero-subtitle">{t('about.hero.subtitle')}</p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="about-story">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="story-content"
          >
            <div className="story-text">
              <h2>{t('about.story.title')}</h2>
              <p>{t('about.story.paragraph1')}</p>
              <p>{t('about.story.paragraph2')}</p>
              <p>{t('about.story.paragraph3')}</p>
            </div>
            <div className="story-image">
              <div className="image-placeholder">🏢</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="about-stats">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="stats-grid"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="stat-card"
              >
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="about-values">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="values-header"
          >
            <h2>{t('about.values.title')}</h2>
            <p>{t('about.values.subtitle')}</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="values-grid"
          >
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="value-card"
                whileHover={{ y: -5 }}
              >
                <div className="value-icon">{value.icon}</div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Team Section */}
      <section className="about-team">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="team-header"
          >
            <h2>{t('about.team.title')}</h2>
            <p>{t('about.team.subtitle')}</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="team-grid"
          >
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="team-card"
                whileHover={{ y: -5 }}
              >
                <div className="member-image">{member.image}</div>
                <h3>{member.name}</h3>
                <p className="member-position">{member.position}</p>
                <p className="member-description">{member.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="about-mission">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mission-content"
          >
            <div className="mission-text">
              <h2>{t('about.mission.title')}</h2>
              <p>{t('about.mission.description')}</p>
            </div>
            <div className="mission-image">
              <div className="image-placeholder">🎯</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="cta-content"
          >
            <h2>{t('about.cta.title')}</h2>
            <p>{t('about.cta.description')}</p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="cta-button"
            >
              {t('about.cta.button')}
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;

